/* qua.domains — Study 3 hero field
   Flowing contour-like streamlines (traced through a 2D flow field) on the
   LEFT + RIGHT edges, a calm centre, plus one continuous cable that runs from
   the status card across the top, down the far-left edge, and into the Systems
   rail below. Mostly neutral grey with faint blue/orange hints.

   All tunables are read live from window.QUA_LINES.hero (see line-config.js)
   so the Line Studio editor can reshape it on the page; it re-builds whenever
   the config changes and persists across reloads. Canvas pointer-events:none. */
(function () {
  var hero = document.querySelector(".page--s3 .hero");
  if (!hero) return;
  var canvas = hero.querySelector(".hero__field");
  if (!canvas) {
    canvas = document.createElement("canvas");
    canvas.className = "hero__field";
    canvas.setAttribute("aria-hidden", "true");
    var grid = hero.querySelector(".hero__grid");
    hero.insertBefore(canvas, grid ? grid.nextSibling : hero.firstChild);
  }
  var ctx = canvas.getContext("2d");
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var NAVY = "22,49,74";
  var W = 0, H = 0, DPR = 1, flows = [], micro = [], cable = null, cableLen = 0;
  var ptr = { x: 0.7, y: 0.4 }, ease = { x: 0.7, y: 0.4 }, has = false;
  var t = 0, last = performance.now(), raf = null, alive = true, inview = true;

  function cfg() { return (window.QUA_LINES && window.QUA_LINES.get().hero) || {}; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  // small deterministic PRNG so a given seed always yields the same field
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var x = Math.imul(a ^ a >>> 15, 1 | a);
      x = x + Math.imul(x ^ x >>> 7, 61 | x) ^ x;
      return ((x ^ x >>> 14) >>> 0) / 4294967296;
    };
  }

  // smooth 2D direction field → organic, non-parallel curvature
  function angle(x, y, c) {
    /* normalise in ABSOLUTE, centre-anchored space (fixed 1280 ref) so the
       flow-field shape stays put on resize/zoom instead of reshuffling */
    var sc = c.scale || 1, nx = ((x - W / 2) / 1280) * sc, ny = ((y - H / 2) / 1280) * sc;
    var base = Math.PI * (
      0.75 * Math.sin(nx * 3.1 + ny * 2.3) +
      0.50 * Math.sin(ny * 4.0 - nx * 1.7 + 1.3) +
      0.30 * Math.sin((nx + ny) * 5.2 - 0.6)
    );
    return base * (c.turbulence == null ? 1 : c.turbulence) + (c.bias || 0) * Math.PI;
  }
  function trace(sx, sy, dir, steps, c) {
    var pts = [], x = sx, y = sy, step = c.step || 9;
    for (var i = 0; i < steps; i++) {
      pts.push([x, y]);
      var a = angle(x, y, c);
      x += Math.cos(a) * step * dir; y += Math.sin(a) * step * dir;
      if (x < -60 || x > W + 60 || y < -60 || y > H + 60) break;
    }
    return pts;
  }

  function build() {
    var c = cfg();
    var r = hero.getBoundingClientRect();
    W = r.width; H = r.height; DPR = Math.min(window.devicePixelRatio || 1, 2);
    if (!W || !H) return;
    canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

    var rng = mulberry32((c.seed || 1) * 1000 + 7);
    var inset = c.sideInset || 0;
    var hidden = c.hidden || [];   // individually deleted streamlines (by stable seed index)

    // seed streamlines pinned to the content-column edges with FIXED pixel
    // spreads (not viewport fractions) so they stay glued to the content and
    // never drift / spread outward when the window width changes
    flows = [];
    var seeds = [];
    var cL = W * 0.16, cR = W * 0.70;
    var innerEl = hero.querySelector(".hero__inner");
    if (innerEl) { var ir = innerEl.getBoundingClientRect(); cL = ir.left - r.left; cR = ir.right - r.left; }
    var io = inset * 1000;                       // optional fixed-px nudge from the studio
    var rowsL = Math.max(0, Math.round(c.leftCount)), rowsR = Math.max(0, Math.round(c.rightCount));
    for (var i = 0; i < rowsL; i++) seeds.push({ x: cL - 24 - io - rng() * 230, y: H * (rowsL < 2 ? 0.5 : i / (rowsL - 1)) + (rng() - 0.5) * 30 });
    for (var j = 0; j < rowsR; j++) seeds.push({ x: cR + 24 + io + rng() * 250, y: H * (rowsR < 2 ? 0.5 : j / (rowsR - 1)) + (rng() - 0.5) * 30 });
    var rBlue = c.accentRatioBlue == null ? 0.14 : c.accentRatioBlue;
    var rOr = c.accentRatioOrange == null ? 0.10 : c.accentRatioOrange;
    seeds.forEach(function (s, idx) {
      var fwd = trace(s.x, s.y, 1, c.lenFwd, c), bwd = trace(s.x, s.y, -1, c.lenBack, c).reverse();
      var pts = bwd.concat(fwd);
      var ru = rng(), accent = ru < (rBlue + rOr);
      flows.push({
        id: idx,
        hidden: hidden.indexOf(idx) >= 0,
        pts: pts,
        col: ru < rBlue ? c.colBlue : (accent ? c.colOrange : c.colMain),
        w: accent ? c.widthAccent : c.width,
        a: accent ? c.opacityAccent : c.opacity,
        ph: rng() * 6.28
      });
    });

    micro = [];
    var M = Math.max(0, Math.round(c.micro));
    var halfInner = (cR - cL) / 2, midX = (cL + cR) / 2;
    for (var m = 0; m < M; m++) {
      // pin to content centre with fixed-px offsets so marks don't drift on resize
      var side = rng() < 0.5 ? -1 : 1;
      var xPx = midX + side * (halfInner + 20 + rng() * 230);
      var ru2 = rng();
      micro.push({ x: xPx, y: rng() * H, type: ru2 < 0.6 ? "dot" : "plus", col: ru2 < 0.18 ? c.colBlue : (ru2 < 0.3 ? c.colOrange : c.colMain), ph: rng() * 6.28 });
    }

    buildCable(c, r);
  }

  function buildCable(c, r) {
    if (c.cablePts && c.cablePts.length >= 2) {
      // user-edited path, stored as fractions of the hero box
      cable = c.cablePts.map(function (p) { return [p[0] * W, p[1] * H]; });
    } else {
      // auto-route: status card → across the top → down far-left edge → into Systems
      var s = hero.querySelector(".status"), sx = W * 0.62, sy = H * 0.42;
      if (s) { var sr = s.getBoundingClientRect(); sx = sr.left - r.left; sy = sr.top - r.top + 40; }
      var eb = hero.querySelector(".eyebrow--tag"), ey = H * 0.30;
      if (eb) { var er = eb.getBoundingClientRect(); ey = er.top - r.top - 16; }
      var leftX = 14, topY = Math.max(18, ey - 26);
      cable = [[leftX, H + 30], [leftX, topY], [sx, topY], [sx, sy]];
    }
    cableLen = 0; for (var k = 1; k < cable.length; k++) cableLen += Math.hypot(cable[k][0] - cable[k - 1][0], cable[k][1] - cable[k - 1][1]);
  }

  function roundedPath(pts, rad) {
    var p = new Path2D(); p.moveTo(pts[0][0], pts[0][1]);
    for (var i = 1; i < pts.length - 1; i++) {
      var a = pts[i - 1], cc = pts[i], n = pts[i + 1];
      var v1x = a[0] - cc[0], v1y = a[1] - cc[1], v2x = n[0] - cc[0], v2y = n[1] - cc[1];
      var l1 = Math.hypot(v1x, v1y) || 1, l2 = Math.hypot(v2x, v2y) || 1, rr = Math.min(rad, l1 / 2, l2 / 2);
      p.lineTo(cc[0] + v1x / l1 * rr, cc[1] + v1y / l1 * rr);
      p.quadraticCurveTo(cc[0], cc[1], cc[0] + v2x / l2 * rr, cc[1] + v2y / l2 * rr);
    }
    p.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]); return p;
  }
  function pointAt(pts, frac) {
    var total = cableLen * frac, acc = 0;
    for (var i = 1; i < pts.length; i++) {
      var seg = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      if (acc + seg >= total) { var u = (total - acc) / seg; return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * u, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * u]; }
      acc += seg;
    }
    return pts[pts.length - 1];
  }

  function draw(now) {
    var c = cfg();
    var dt = Math.min((now - last) / 1000, 0.05); last = now;
    var spd = c.speed == null ? 1 : c.speed;
    if (!reduce) { t += dt * spd; ease.x += (ptr.x - ease.x) * 0.07; ease.y += (ptr.y - ease.y) * 0.07; }
    var mx = ease.x * W, my = ease.y * H, on = has;
    ctx.clearRect(0, 0, W, H);

    /* organic streamlines (subtle drift + cursor brighten) */
    var wob = reduce ? 0 : (c.drift == null ? 2.2 : c.drift);
    for (var f = 0; f < flows.length; f++) {
      var fl = flows[f], pts = fl.pts; if (fl.hidden || pts.length < 2) continue;
      ctx.beginPath();
      for (var i = 0; i < pts.length; i++) {
        var ddx = reduce ? 0 : Math.sin(t * 0.5 + fl.ph + i * 0.08) * wob;
        var ddy = reduce ? 0 : Math.cos(t * 0.4 + fl.ph + i * 0.08) * wob;
        var x = pts[i][0] + ddx, y = pts[i][1] + ddy;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      var nearC = on ? clamp(1 - Math.hypot(pts[(pts.length / 2) | 0][0] - mx, pts[(pts.length / 2) | 0][1] - my) / 220, 0, 1) : 0;
      ctx.strokeStyle = "rgba(" + fl.col + "," + (fl.a + nearC * 0.3).toFixed(3) + ")";
      ctx.lineWidth = fl.w; ctx.stroke();
    }

    /* minimal micro marks */
    for (var mm = 0; mm < micro.length; mm++) {
      var mk = micro[mm];
      var tw = reduce ? 0.6 : 0.4 + 0.6 * Math.sin(t * 1.2 + mk.ph);
      ctx.strokeStyle = ctx.fillStyle = "rgba(" + mk.col + "," + (0.2 + tw * 0.3).toFixed(3) + ")";
      if (mk.type === "dot") { ctx.beginPath(); ctx.arc(mk.x, mk.y, 1.2, 0, 6.2832); ctx.fill(); }
      else { ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(mk.x - 3, mk.y); ctx.lineTo(mk.x + 3, mk.y); ctx.moveTo(mk.x, mk.y - 3); ctx.lineTo(mk.x, mk.y + 3); ctx.stroke(); }
    }

    /* freehand user-drawn lines (hand-drawn with the Line Studio pen) */
    var ul = c.userLines || [];
    if (ul.length) {
      ctx.strokeStyle = "rgba(" + (c.drawColor || "90,104,120") + ",0.72)";
      ctx.lineWidth = c.drawWidth == null ? 1.5 : c.drawWidth;
      ctx.lineJoin = "round"; ctx.lineCap = "round";
      for (var u = 0; u < ul.length; u++) {
        var ln = ul[u]; if (!ln || ln.length < 2) continue;
        ctx.beginPath();
        for (var p = 0; p < ln.length; p++) { var ux = ln[p][0] * W, uy = ln[p][1] * H; if (p === 0) ctx.moveTo(ux, uy); else ctx.lineTo(ux, uy); }
        ctx.stroke();
      }
    }

    /* continuous cable + travelling nodes */
    if (cable) {
      ctx.strokeStyle = "rgba(" + c.cableColor + "," + (c.cableOpacity == null ? 0.3 : c.cableOpacity) + ")";
      ctx.lineWidth = c.cableWidth == null ? 1.4 : c.cableWidth;
      ctx.stroke(roundedPath(cable, c.cableRadius == null ? 28 : c.cableRadius));
      var endp = cable[cable.length - 1];
      ctx.beginPath(); ctx.arc(endp[0], endp[1], 3, 0, 6.2832); ctx.fillStyle = "rgba(" + c.colBlue + ",0.7)"; ctx.fill();
      if (!reduce) {
        var np = Math.max(0, Math.round(c.cablePackets == null ? 2 : c.cablePackets));
        var psz = c.cablePacketSize == null ? 1 : c.cablePacketSize;
        var pspd = c.cablePacketSpeed == null ? 1 : c.cablePacketSpeed;
        for (var sp = 0; sp < np; sp++) {
          var frac = ((t * 0.07 * pspd + sp / Math.max(1, np)) % 1 + 1) % 1, pp = pointAt(cable, frac), col = sp % 2 ? c.colBlue : c.colOrange;
          ctx.beginPath(); ctx.arc(pp[0], pp[1], 5 * psz, 0, 6.2832); ctx.fillStyle = "rgba(" + col + ",0.12)"; ctx.fill();
          ctx.beginPath(); ctx.arc(pp[0], pp[1], 2.2 * psz, 0, 6.2832); ctx.fillStyle = "rgba(" + col + ",0.9)"; ctx.fill();
        }
      }
    }
  }

  function loop(now) {
    if (!alive) return;
    /* size-poll: window 'resize' and ResizeObserver are unreliable inside the
       embed iframe, but the rAF loop runs every frame. Detect a real hero box
       size change here and rebuild so the field stays locked to the content
       instead of the stale bitmap being CSS-stretched (which looked like drift). */
    var r = hero.getBoundingClientRect();
    if (Math.abs(r.width - W) > 1 || Math.abs(r.height - H) > 1) {
      rebuild();
      // re-render the system rail in lockstep so it stays locked to the content
      if (window.__quaRail && window.__quaRail.render) window.__quaRail.render();
    }
    draw(now); raf = requestAnimationFrame(loop);
  }
  function startLoop() { if (alive && inview && !raf) { last = performance.now(); raf = requestAnimationFrame(loop); } }
  function stopLoop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

  if (!reduce) {
    hero.addEventListener("pointermove", function (e) {
      var r = hero.getBoundingClientRect();
      ptr.x = clamp((e.clientX - r.left) / r.width, 0, 1);
      ptr.y = clamp((e.clientY - r.top) / r.height, 0, 1);
      has = true;
    });
    hero.addEventListener("pointerleave", function () { has = false; });
  }

  function rebuild() { build(); if (reduce) draw(performance.now()); }
  // expose for the Line Studio (geometry + live redraw on config change)
  window.__quaHeroField = {
    rebuild: rebuild,
    redraw: function () { draw(performance.now()); },
    getCable: function () { return cable; },
    flows: function () { return flows; },
    heroEl: hero,
    size: function () { return { W: W, H: H }; }
  };

  rebuild();
  [180, 480, 900].forEach(function (ms) { setTimeout(rebuild, ms); });
  window.addEventListener("load", rebuild);
  window.addEventListener("resize", function () { clearTimeout(window.__s3hf); window.__s3hf = setTimeout(rebuild, 120); });
  /* ResizeObserver is the reliable signal inside an iframe — window 'resize'
     often never reaches an embedded frame, so the canvas backing-store would
     go stale and the CSS-stretched bitmap made the lines appear to drift.
     Observing the hero box keeps the field locked to the content on resize. */
  if (window.ResizeObserver) {
    var __ro = new ResizeObserver(function () { clearTimeout(window.__s3hf); window.__s3hf = setTimeout(rebuild, 80); });
    __ro.observe(hero);
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(rebuild);
  if (window.QUA_LINES) window.QUA_LINES.onChange(function (scope) { if (scope === "hero") { rebuild(); if (reduce) draw(performance.now()); } });

  if (!reduce) {
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (en) { inview = en[0].isIntersecting; if (inview) startLoop(); else stopLoop(); }, { threshold: 0 }).observe(hero);
    }
    startLoop();
  }
})();
